import * as React from 'react';
import * as Immutable from 'immutable';
import { combineReducers } from 'redux-immutable';
import AccountStore, { AccountStoreFactory, IAccountStore } from '../../_stores/Account/AccountStore';
import { AccountStoreAction, AccountStoreActionType } from '../../_actions/Account/AccountStoreActions';
import ListingStore from '../../../UserData/_stores/Store/ListingStore';
import VariantPriceStore from '../../../UserData/_stores/Store/VariantPriceStore';
import PurchaseReportStore from '../../../UserData/_stores/Store/PurchaseReportStore';
import { StoreProductListingAction, StoreProductListingActionType } from '../../../UserData/_actions/Store/StoreProductListingActions';
import { StorePurchaseReportAction, StorePurchaseReportActionType } from '../../../UserData/_actions/Store/StorePurchaseReportActions';

type AccountReducerAction = AccountStoreAction | StoreProductListingAction | StorePurchaseReportAction;

const getListingId = (id_product_design: number) => String(id_product_design);

type SelectionFieldSet = {
  all: keyof Pick<IAccountStore, 'productDesignSelectedAllProducts' | 'imageSelectedAllImages' | 'storeSelectedAllProducts'>
  clearCounter: keyof Pick<IAccountStore, 'productDesignSelectionClearCounter' | 'imageSelectionClearCounter' | 'storeProductSelectionClearCounter'>
  excluded: keyof Pick<IAccountStore, 'productDesignExcludedSelectedProducts' | 'imageExcludedSelectedImages' | 'storeExcludedSelectedProducts'>
  selected: keyof Pick<IAccountStore, 'productDesignSelectedProducts' | 'imageSelectedImages' | 'storeSelectedProducts'>
}

const updateSelectedItems = (store: AccountStore, fields: SelectionFieldSet, ids: number[], selected: boolean) => {
  if (ids.length === 0) {
    return store;
  }

  const selectedAll = store.get(fields.all)
  const selectionKey = selectedAll ? fields.excluded : fields.selected
  const selectedProducts = store.get(selectionKey)
  const shouldAdd = selected !== selectedAll
  const nextSelectedProducts = selectedProducts.withMutations(selectedProducts => {
    ids.forEach(id => {
      if (shouldAdd) {
        selectedProducts.add(id)
      } else {
        selectedProducts.remove(id)
      }
    })
  })

  return Immutable.is(selectedProducts, nextSelectedProducts)
    ? store
    : store.set(selectionKey, nextSelectedProducts)
}

const clearSelectedItems = (store: AccountStore, fields: SelectionFieldSet) => (
  store
    .set(fields.selected, Immutable.OrderedSet<number>())
    .set(fields.all, false)
    .set(fields.excluded, Immutable.OrderedSet<number>())
    .set(fields.clearCounter, store.get(fields.clearCounter) + 1)
)

const productDesignSelectionFields: SelectionFieldSet = {
  all: 'productDesignSelectedAllProducts',
  clearCounter: 'productDesignSelectionClearCounter',
  excluded: 'productDesignExcludedSelectedProducts',
  selected: 'productDesignSelectedProducts',
}

const imageSelectionFields: SelectionFieldSet = {
  all: 'imageSelectedAllImages',
  clearCounter: 'imageSelectionClearCounter',
  excluded: 'imageExcludedSelectedImages',
  selected: 'imageSelectedImages',
}

const storeProductSelectionFields: SelectionFieldSet = {
  all: 'storeSelectedAllProducts',
  clearCounter: 'storeProductSelectionClearCounter',
  excluded: 'storeExcludedSelectedProducts',
  selected: 'storeSelectedProducts',
}

const AccountReducer = (
  _store: AccountStore = new AccountStore(),
  action: AccountReducerAction
): AccountStore => {
  switch (action.type) {
    case AccountStoreActionType.SetProductDesignSelectedProducts:
      _store = updateSelectedItems(_store, productDesignSelectionFields, action.ids, action.selected)
      break;

    case AccountStoreActionType.SelectAllProductDesignSelectedProduct:
      _store = _store
        .set('productDesignSelectedProducts', Immutable.OrderedSet<number>())
        .set('productDesignSelectedAllProducts', true)
        .set('productDesignExcludedSelectedProducts', Immutable.OrderedSet<number>())
      break;

    case AccountStoreActionType.ClearProductDesignSelectedProduct:
      _store = clearSelectedItems(_store, productDesignSelectionFields)
      break;

    case AccountStoreActionType.SetImageSelectedImages:
      _store = updateSelectedItems(_store, imageSelectionFields, action.ids, action.selected)
      break;

    case AccountStoreActionType.SelectAllImageSelectedImage:
      _store = _store
        .set('imageSelectedImages', Immutable.OrderedSet<number>())
        .set('imageSelectedAllImages', true)
        .set('imageExcludedSelectedImages', Immutable.OrderedSet<number>())
      break;

    case AccountStoreActionType.ClearImageSelectedImage:
      _store = clearSelectedItems(_store, imageSelectionFields)
      break;

    case AccountStoreActionType.SetStoreSelectedProducts:
      _store = updateSelectedItems(_store, storeProductSelectionFields, action.ids, action.selected)
      break;

    case AccountStoreActionType.SelectAllStoreSelectedProduct:
      _store = _store
        .set('storeSelectedProducts', Immutable.OrderedSet<number>())
        .set('storeSelectedAllProducts', true)
        .set('storeExcludedSelectedProducts', Immutable.OrderedSet<number>())
      break;

    case AccountStoreActionType.ClearStoreSelectedProduct:
      _store = clearSelectedItems(_store, storeProductSelectionFields)
      break;

    case StoreProductListingActionType.EditProductListing:
      _store = _store.withMutations(store => {
        const listing = new ListingStore({
          id: getListingId(action.id_product_design),
          id_product_design: action.id_product_design,
          id_product: action.id_product,
          title: action.title,
          description: action.description,
          price: action.price,
          update_preset: action.update_preset || false,
          disabled_preview_ids: Immutable.List<number>(action.disabled_preview_ids || []),
          disabled_option_ids: Immutable.List<number>(action.disabled_option_ids || []),
          variant_prices: action.variant_prices || Immutable.Map<string, VariantPriceStore>(),
          squarespace_store_page_id: action.squarespace_store_page_id,
          is_modified: action.is_modified,
        });

        store.setIn(['storeProductListings', listing.get('id')], listing);
      });
      break;

    case StoreProductListingActionType.RemoveProductListing:
      _store = _store.removeIn(['storeProductListings', action.id]);
      break;

    case StoreProductListingActionType.SelectProductListing:
      _store = _store.set('storeProductSelectedListing', action.id);
      break;

    case StoreProductListingActionType.ResetProductListings:
      _store = _store
        .set('storeProductListings', Immutable.Map<string, ListingStore>())
        .set('storeProductSelectedListing', "");
      break;

    case StorePurchaseReportActionType.EditPurchaseReportData:
      _store = _store.setIn(['storePurchaseReports', action.store], new PurchaseReportStore(action.report));
      break;
  }

  const defaultAccount = new AccountStore
  return new AccountStore(combineReducers<IAccountStore, IAccountStore>({
    productDesignSelectedProducts: (_store) => _store ?? defaultAccount.get('productDesignSelectedProducts'),
    productDesignSelectedAllProducts: (_store) => _store ?? defaultAccount.get('productDesignSelectedAllProducts'),
    productDesignExcludedSelectedProducts: (_store) => _store ?? defaultAccount.get('productDesignExcludedSelectedProducts'),
    productDesignSelectionClearCounter: (_store) => _store ?? defaultAccount.get('productDesignSelectionClearCounter'),
    imageSelectedImages: (_store) => _store ?? defaultAccount.get('imageSelectedImages'),
    imageSelectedAllImages: (_store) => _store ?? defaultAccount.get('imageSelectedAllImages'),
    imageExcludedSelectedImages: (_store) => _store ?? defaultAccount.get('imageExcludedSelectedImages'),
    imageSelectionClearCounter: (_store) => _store ?? defaultAccount.get('imageSelectionClearCounter'),
    storeSelectedProducts: (_store) => _store ?? defaultAccount.get('storeSelectedProducts'),
    storeSelectedAllProducts: (_store) => _store ?? defaultAccount.get('storeSelectedAllProducts'),
    storeExcludedSelectedProducts: (_store) => _store ?? defaultAccount.get('storeExcludedSelectedProducts'),
    storeProductSelectionClearCounter: (_store) => _store ?? defaultAccount.get('storeProductSelectionClearCounter'),
    storeProductListings: (_store) => _store ?? defaultAccount.get('storeProductListings'),
    storeProductSelectedListing: (_store) => _store ?? defaultAccount.get('storeProductSelectedListing'),
    storePurchaseReports: (_store) => _store ?? defaultAccount.get('storePurchaseReports'),
  }, AccountStoreFactory)(_store, action));
}

export default AccountReducer
